import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import { CURRENCY_LABELS, CurrencyCode, SUPPORTED_CURRENCIES } from "@/lib/config/currency";
import { useCurrency } from "@/hooks/use-currency";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

interface CurrencySelectorProps {
  className?: string;
  variant?: "compact" | "block";
}

export const CurrencySelector = ({ className, variant = "compact" }: CurrencySelectorProps) => {
  const { currency, setCurrency } = useCurrency();
  const [open, setOpen] = useState(false);

  const handleSelectCurrency = (nextCurrency: CurrencyCode) => {
    if (nextCurrency === currency) {
      setOpen(false);
      return;
    }
    setCurrency(nextCurrency);
    setOpen(false);
  };

  if (variant === "compact") {
    return (
      <div className={cn("shrink-0", className)}>
        <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              aria-label="Choisir la devise d'affichage"
              aria-expanded={open}
              className="h-9 w-[92px] rounded-full border-border bg-muted/40 px-3 text-xs font-semibold tracking-wide gap-1"
            >
              <span>{CURRENCY_LABELS[currency]}</span>
              <ChevronDown className="h-3.5 w-3.5 opacity-60" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={6}
            className="z-[120] min-w-[7rem] rounded-xl border-border p-1 shadow-lg"
          >
            {SUPPORTED_CURRENCIES.map((code) => (
              <DropdownMenuItem
                key={code}
                className="cursor-pointer rounded-lg text-xs font-semibold tracking-wide"
                onSelect={() => handleSelectCurrency(code)}
              >
                <span className="flex w-full items-center justify-between gap-2">
                  {CURRENCY_LABELS[code]}
                  {currency === code ? <Check className="h-3.5 w-3.5 text-green-night" aria-hidden /> : null}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label="Choisir la devise d'affichage"
      className={cn("grid grid-cols-3 gap-2", className)}
    >
      {SUPPORTED_CURRENCIES.map((code) => {
        const isActive = currency === code;
        return (
          <Button
            key={code}
            type="button"
            variant="ghost"
            size="sm"
            aria-label={`Choisir ${code}`}
            aria-pressed={isActive}
            onClick={() => handleSelectCurrency(code)}
            className={cn(
              "h-9 rounded-lg border text-xs font-semibold tracking-wide transition-colors",
              isActive
                ? "border-green-night bg-emerald-soft text-green-night hover:bg-emerald-soft hover:text-green-night"
                : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
            )}
          >
            {CURRENCY_LABELS[code]}
          </Button>
        );
      })}
    </div>
  );
};
